import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuth } from "../auth/useAuth";
import { useRuntimeConfig } from "../config/useRuntimeConfig";
import { createMedicationServiceClient, type Medication, type UpcomingDose } from "./client";

export type MedicationOverviewStatus = "idle" | "loading" | "ready" | "error";

// Read-only aggregate loader for composite pages that need medication records AND upcoming doses
// together (the Health landing page). One request to medication-service's GET /v1/overview
// replaces the separate GET /v1/medications + GET /v1/doses/upcoming loads -- the backend applies
// the same ordering / filtering / timezone / occurrence semantics and JWT / health_profile_id
// ownership as those endpoints. Same hook conventions as useMedications: runtime-configured base
// URL, auth.user access token, AbortSignal, loading/error state, no cache.
export function useMedicationOverview() {
  const auth = useAuth();
  const config = useRuntimeConfig();
  const apiBaseUrl = config?.medicationService?.apiBaseUrl;
  const [medications, setMedications] = useState<Medication[]>([]);
  const [upcomingDoses, setUpcomingDoses] = useState<UpcomingDose[]>([]);
  const [status, setStatus] = useState<MedicationOverviewStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const client = useMemo(() => {
    if (!apiBaseUrl) {
      return null;
    }

    return createMedicationServiceClient(apiBaseUrl, () => auth.user?.access_token);
  }, [apiBaseUrl, auth.user]);

  const loadMedicationOverview = useCallback(
    async (signal?: AbortSignal) => {
      if (!client) {
        setError("Medication records are not available yet.");
        setStatus("error");
        return;
      }

      setStatus("loading");
      setError(null);

      try {
        const overview = await client.listMedicationOverview(signal);
        setMedications(overview.medications);
        setUpcomingDoses(overview.upcomingDoses);
        setStatus("ready");
      } catch (nextError) {
        if (nextError instanceof DOMException && nextError.name === "AbortError") {
          return;
        }

        setError(nextError instanceof Error ? nextError.message : "We could not load your medication records.");
        setStatus("error");
      }
    },
    [client],
  );

  useEffect(() => {
    if (!auth.isAuthenticated) {
      return undefined;
    }

    const controller = new AbortController();
    void loadMedicationOverview(controller.signal);

    return () => controller.abort();
  }, [auth.isAuthenticated, loadMedicationOverview]);

  return {
    error,
    loadMedicationOverview,
    medications,
    status,
    upcomingDoses,
  };
}
